// Phase machine — pure turn advancement with no mutation.

import type { GameEvent, GameState, PlayerId, TerritoryId, Phase, TeamId } from './types.js'
// The reach predicate: does `from` border the target in the outgoing direction
// (two-way OR one-way). This module exports its own `canAttack` — the full
// validation gate — so the two names must not collide.
import { hasBorderTo, armyHeadroom, canPlaceOn, DICE_COUNT_OUT_OF_RANGE } from '@warcarp/api-types'
import { createRng } from './dice.js'
import { drawCard, handSize } from './cards.js'
import { reinforcementCount } from './reinforce.js'
import type { Card } from './cards.js'
import { activePlayers, activeTeams, sideMembers, sideWinners, survivingWinner, teamResultFor } from './teams.js'

const PHASE_SEQUENCE: readonly Phase[] = ['placement', 'attack', 'fortify']

/**
 * Returns true iff the player owns zero territories.
 */
export function isPlayerEliminated(state: GameState, playerId: PlayerId): boolean {
  return !state.map.territories.some((t) => t.ownerId === playerId)
}

/**
 * Returns true iff a player at the given index in state.players should be skipped.
 */
function isIndexEliminated(state: GameState, index: number): boolean {
  const playerId = state.players[index]
  if (playerId === undefined) return true
  return state.eliminated.includes(playerId) || isPlayerEliminated(state, playerId)
}

/**
 * Advance the game phase by one step.
 *
 * Sequence: reinforce → attack → fortify → (next non-eliminated player's) reinforce
 *
 * Resets attacksThisTurn when entering attack phase.
 * Resets fortifiesThisTurn when entering fortify phase.
 *
 * Pure: never mutates state; returns a new object.
 */
export function advancePhase(state: GameState): GameState {
  // 'post-elim-placement' is an interrupt phase that is not part of the normal
  // sequence. It is resolved by the post-elim-place route (or the bot driver),
  // which sets phase back to 'attack' directly — advancePhase should never be
  // called while in this phase. If it is (defensive), treat it as 'attack'.
  const effectivePhase: Phase = state.phase === 'post-elim-placement' ? 'attack' : state.phase
  const currentPhaseIndex = PHASE_SEQUENCE.indexOf(effectivePhase)

  // If we're not on the last phase, just advance to the next phase.
  if (currentPhaseIndex < PHASE_SEQUENCE.length - 1) {
    const nextPhase = PHASE_SEQUENCE[currentPhaseIndex + 1] as Phase
    const currentPlayer = state.players[state.turnIndex] as PlayerId
    const phaseAdvanceEvent: GameEvent = {
      kind: 'phase-advance',
      from: state.phase,
      to: nextPhase,
      player: currentPlayer,
      ts: Date.now(),
    }
    return {
      ...state,
      phase: nextPhase,
      // Reset per-turn counters when entering the relevant phase.
      attacksThisTurn: nextPhase === 'attack' ? 0 : state.attacksThisTurn,
      fortifiesThisTurn: nextPhase === 'fortify' ? 0 : state.fortifiesThisTurn,
      // Clear any pending post-conquest transfer — leaving the attack
      // phase or advancing past it forfeits the free transfer window.
      lastConquest: null,
      events: [...(state.events ?? []), phaseAdvanceEvent],
    }
  }

  // We're on 'fortify' — wrap to next player's 'placement'.
  const hasActivePlayers = state.players.some(
    (p) => !state.eliminated.includes(p) && !isPlayerEliminated(state, p),
  )

  if (!hasActivePlayers) {
    // Defensive: a caller should have ended the game via shouldEndGame
    // before reaching advancePhase with zero active players. If they
    // didn't, fail loudly — silently no-oping (the previous behaviour)
    // hides the bug and produces a state with a current player who
    // can't actually move.
    throw new Error(
      'advancePhase: no active players remain — caller should have invoked shouldEndGame + applyGameEnd first',
    )
  }

  // Find the next non-eliminated player in the rotation.
  const totalPlayers = state.players.length
  let nextTurnIndex = state.turnIndex
  let steps = 0

  do {
    nextTurnIndex = (nextTurnIndex + 1) % totalPlayers
    steps++
    // Avoid infinite loop if somehow all players are eliminated.
    if (steps > totalPlayers) break
  } while (isIndexEliminated(state, nextTurnIndex))

  // Increment roundsPlayed when the turn wraps back to the first active
  // player (one full pass through all non-eliminated players). Exclude
  // the single-active-player edge case: nextTurnIndex always equals
  // state.turnIndex there, which would otherwise increment the counter
  // every turn. shouldEndGame's `singleSurvivor` branch ends the game
  // before this can compound, but the counter would still be wrong on
  // the way out.
  const activeCount = state.players.filter(
    (p) => !state.eliminated.includes(p) && !isPlayerEliminated(state, p),
  ).length
  const wrappedBackToStart = nextTurnIndex <= state.turnIndex && activeCount > 1
  const newRoundsPlayed = wrappedBackToStart
    ? (state.roundsPlayed ?? 0) + 1
    : (state.roundsPlayed ?? 0)

  // ─── Card draw on turn-end conquest ─────────────────────────────────────
  //
  // Rule: if the player who just finished their turn captured ≥1
  // territory at any point during the turn, they draw exactly one card.
  // `conqueredThisTurn` was flipped true by applyAttack on the first
  // conquering attack of the turn. We draw into the just-ended player's
  // hand here (NOT the next player's) and then clear the flag so the next
  // player starts fresh.
  let nextCards = state.cards
  let nextDeck = state.deck
  let nextDiscard = state.discard
  let nextRngSeed = state.rngSeed
  const justEnded = state.players[state.turnIndex]
  if (
    state.conqueredThisTurn &&
    justEnded !== undefined &&
    state.deck !== undefined
  ) {
    try {
      const rng = createRng(state.rngSeed)
      const draw = drawCard(state.deck, state.discard ?? [], rng)
      const cards: Record<PlayerId, Card[]> = { ...(state.cards ?? {}) }
      const hand = cards[justEnded] ?? []
      cards[justEnded] = [...hand, draw.card]
      nextCards = cards
      nextDeck = draw.deck
      nextDiscard = draw.discard
      nextRngSeed = rng.nextSeed()
    } catch {
      // Empty deck + empty discard is the only failure case. Skip the draw
      // gracefully — it's an extreme edge case (would require every card to
      // be in some hand simultaneously, which the 5-cap precludes in practice).
    }
  }

  // Compute the next player's mustTurnInCards on placement-phase entry.
  // True iff that player's hand has ≥5 cards at the moment they enter
  // placement. The reinforce route uses this + hand.length ≥ 5 to reject
  // placements until the player drops below the cap.
  const nextPlayerId = state.players[nextTurnIndex]
  const nextMustTurnIn =
    nextPlayerId !== undefined && handSize(nextCards ?? {}, nextPlayerId) >= 5

  // ─── Event log ────────────────────────────────────────────────────────────
  const now = Date.now()
  const newEvents: GameEvent[] = [...(state.events ?? [])]

  // Emit phase-advance for the final phase transition (fortify → placement).
  const leavingPlayer = state.players[state.turnIndex] as PlayerId
  newEvents.push({
    kind: 'phase-advance',
    from: 'fortify',
    to: 'placement',
    player: leavingPlayer,
    ts: now,
  } satisfies GameEvent)

  // Emit card-draw if a draw happened for the just-ended player.
  if (state.conqueredThisTurn && justEnded !== undefined && nextCards !== state.cards) {
    newEvents.push({
      kind: 'card-draw',
      player: justEnded,
      ts: now,
    } satisfies GameEvent)
  }

  // Emit turn-start for the next player entering placement.
  if (nextPlayerId !== undefined) {
    newEvents.push({
      kind: 'turn-start',
      // Displayed turn number is 1-indexed (the first round is "Turn 1").
      // `roundsPlayed` stays 0-indexed internally (maxTurns / stall logic);
      // only this log-facing label is shifted.
      turnNumber: newRoundsPlayed + 1,
      player: nextPlayerId,
      // Troops the incoming player receives, so the log can read
      // "Turn 4 — X begins (7 troops received)". Computed off `state` (the
      // pre-advance snapshot): nothing in this function touches the map or the
      // next player's bonusArmies, so this is exactly what the placement route
      // will hand them. A card turn-in during their turn adds more and logs its
      // own row — this number is what they START with.
      armies: reinforcementCount(state, nextPlayerId),
      ts: now,
    } satisfies GameEvent)
  }

  // Grudges fade: halve everything recorded AGAINST the incoming player once per
  // cycle, so the ledger reads "recent" rather than "ever". Entries that decay
  // below a quarter-roll are dropped to keep the blob small.
  const decayedAggression = nextPlayerId !== undefined && state.aggression
    ? Object.fromEntries(Object.entries(state.aggression).map(([attacker, row]) => {
        const hits = row[nextPlayerId]
        if (hits === undefined) return [attacker, row]
        const { [nextPlayerId]: _drop, ...rest } = row
        const faded = hits / 2
        return [attacker, faded >= 0.25 ? { ...rest, [nextPlayerId]: faded } : rest]
      }))
    : state.aggression

  return {
    ...state,
    turnIndex: nextTurnIndex,
    phase: 'placement',
    ...(decayedAggression !== state.aggression ? { aggression: decayedAggression } : {}),
    // Both counters reset on new turn (next player's first phase is reinforce,
    // but we also reset attack/fortify here for clarity).
    attacksThisTurn: 0,
    fortifiesThisTurn: 0,
    roundsPlayed: newRoundsPlayed,
    lastConquest: null,
    // Card-related fields. Always clear conqueredThisTurn at turn boundary.
    conqueredThisTurn: false,
    mustTurnInCards: nextMustTurnIn,
    events: newEvents,
    ...(nextCards !== state.cards ? { cards: nextCards } : {}),
    ...(nextDeck !== state.deck ? { deck: nextDeck } : {}),
    ...(nextDiscard !== state.discard ? { discard: nextDiscard } : {}),
    ...(nextRngSeed !== state.rngSeed ? { rngSeed: nextRngSeed } : {}),
  }
}

// ─── GamePlayer stub — the engine receives only the minimal projection ────────

/**
 * Minimal projection of a game player needed by `shouldEndGame`.
 * The route layer passes the full DB row; we only care about slot + isBot.
 */
export interface GamePlayerStub {
  slot: number
  isBot: boolean
}

/**
 * Pure, fully-testable helper that decides whether the game should end
 * and (if so) who won.
 *
 * Rules checked, in priority order:
 *  1. `stalled` — maxTurns cap fired.
 *  2. `singleSurvivor` — only one non-eliminated player remains (post-claim).
 *  3. `onlyBotsRemaining` — all non-eliminated players are bots (bot-vs-bot
 *     is pointless; auto-end with the territory leader as the winner, or
 *     null when bots are tied for the lead).
 *
 * Returns:
 *  - `ended: false` when none of the above apply.
 *  - `ended: true` with `reason` and `winner` (null when tied/no-winner).
 *
 * Callers pass `gamePlayers` (DB-derived, keyed by `slot`) so the engine
 * can check the `isBot` flag without touching the database itself.
 */
/**
 * A finished game. `winnerTeam` / `winners` are present only in team games
 * (see engine/teams.ts `teamResultFor`).
 */
export interface GameEndResult {
  ended: true
  reason: 'last-standing' | 'stalled' | 'resign'
  winner: PlayerId | null
  winnerTeam?: TeamId | null
  winners?: PlayerId[]
}

export function shouldEndGame(
  state: GameState,
  gamePlayers: GamePlayerStub[],
  // Always on, except a local QA all-bot game (lib/botsFogTeamsQa.ts).
  options: { onlyBotsRule?: boolean } = {},
): { ended: false } | GameEndResult {
  const stalledResult = isGameStalled(state)

  if (stalledResult.stalled) {
    const { winner } = computeStalledWinner(state)
    return { ended: true, reason: 'stalled', winner, ...teamResultFor(state, winner) }
  }

  // 'claim' is the one phase where territory ownership is still incomplete: a
  // player who hasn't picked their first territory owns 0 of them, so
  // isPlayerEliminated() falsely flags them as eliminated. BOTH territory-based
  // victory checks below (single-survivor AND only-bots) would mis-fire, so
  // skip them during claim and rely on the stall check above.
  //
  // BUG this guards against: in a human-vs-bot game with Manual claim, if the
  // bot wins the turn-order shuffle and claims first, the human still owns 0
  // territories → active === [bot] → the only-bots branch ended the game
  // instantly with a bogus win, right after the first claim. The single-
  // survivor branch was already guarded against 'claim'; the only-bots branch
  // was NOT — this extends the guard to cover both.
  //
  // NOTE: post-elim-placement is deliberately NOT skipped here — conquering
  // your last opponent transitions to post-elim-placement, and the game must
  // still end (single survivor) at that point. (botDriver.isGameOver skips it
  // as a loop-exit optimisation, but this route-side check is authoritative.)
  if (state.phase === 'claim') {
    return { ended: false }
  }

  const active = activePlayers(state)

  // One side left (a team, or a player in free-for-all) ends the game. In a
  // team game every member of that team wins, eliminated members included;
  // `winner` is the first of them in turn order.
  if (activeTeams(state).length <= 1) {
    const survivor = survivingWinner(state) ?? null
    return { ended: true, reason: 'last-standing', winner: survivor, ...teamResultFor(state, survivor) }
  }

  // Only-bots-remaining: every surviving player is a bot. The last
  // human has been eliminated or resigned, so the game has no live
  // viewer — end it now instead of spinning forever. Winner = the bot
  // with the most territories (computeStalledWinner returns null on a
  // tie, which we surface unchanged). Stats recording filters out bots,
  // so crediting a "bot vs bot" win doesn't pollute leaderboards.
  // Reuses reason: 'stalled' so the game-end event payload stays
  // backwards-compatible with existing consumers.
  const onlyBots = active.every((pid) => {
    const slot = parseInt(pid, 10)
    const sp = gamePlayers.find((s) => s.slot === slot)
    return sp?.isBot === true
  })
  if (onlyBots && options.onlyBotsRule !== false) {
    const { winner } = computeStalledWinner(state)
    return { ended: true, reason: 'stalled', winner, ...teamResultFor(state, winner) }
  }

  return { ended: false }
}

/**
 * Append a `game-end` event to `state.events` and return the new state.
 *
 * Called by the route layer (commitStateChange) once it has determined that
 * the game has ended (single survivor, stall, or resign). This keeps the
 * engine as the sole event-appending authority.
 *
 * Pure: never mutates `state`; returns a new object.
 */
export function applyGameEnd(
  state: GameState,
  winner: PlayerId | null,
  reason: 'last-standing' | 'stalled' | 'resign',
): GameState {
  // Team games also carry `winnerTeam` and every winner (derived from
  // `winner` and state.teams); free-for-all events are unchanged.
  const gameEndEvent: GameEvent = {
    kind: 'game-end',
    winner,
    reason,
    ...teamResultFor(state, winner),
    ts: Date.now(),
  }
  return {
    ...state,
    events: [...(state.events ?? []), gameEndEvent],
  }
}

/**
 * Returns whether the game has been force-terminated by the maxTurns cap.
 */
export function isGameStalled(state: GameState): { stalled: boolean; reason?: 'turns-cap' } {
  const { maxTurns } = state.config
  if (maxTurns !== null && (state.roundsPlayed ?? 0) >= maxTurns) {
    return { stalled: true, reason: 'turns-cap' }
  }
  return { stalled: false }
}

/**
 * Determines the winner (or tied players) when the game is force-terminated.
 * Counts territories owned by each non-eliminated player.
 * Returns the unique leader as winner, or tied[] if multiple players tie for the lead.
 *
 * Team games rank the surviving teams by their members' combined
 * territories, then combined troops, then combined next-turn income (each
 * member's `reinforcementCount` with region bonuses but without pending
 * `bonusArmies`). The leading team wins and `winner` is its first member in
 * turn order who didn't resign; teams still level
 * on all three are a tie, and `tied` lists every member of the tied teams.
 * The maintainer decided this tiebreak on 2026-11-01 (docs/team-games-plan.md
 * §12). Free-for-all keeps territories only.
 */
export function computeStalledWinner(state: GameState): { winner: PlayerId | null; tied: PlayerId[] } {
  const active = activePlayers(state)

  if (active.length === 0) {
    return { winner: null, tied: [] }
  }

  if (state.teams) {
    // Next-turn income (decision 4): territory and region income only. Pending
    // bonus armies (card turn-ins, elimination bonuses) are not income.
    const withoutBonuses: GameState = { ...state, bonusArmies: {} }
    // Each tiebreak narrows the leaders to the teams with the top score.
    const tiebreaks: Array<(members: PlayerId[]) => number> = [
      (members) => state.map.territories.filter((t) => members.includes(t.ownerId)).length,
      (members) => state.map.territories.reduce((sum, t) => sum + (members.includes(t.ownerId) ? t.armies : 0), 0),
      (members) => members.reduce((sum, p) => sum + reinforcementCount(withoutBonuses, p), 0),
    ]
    let leading = activeTeams(state) as string[]
    for (const score of tiebreaks) {
      if (leading.length <= 1) break
      const scores = new Map(leading.map((side) => [side, score(sideMembers(state, side))]))
      const top = Math.max(...scores.values())
      leading = leading.filter((side) => scores.get(side) === top)
    }
    if (leading.length === 1) {
      return { winner: sideWinners(state, leading[0]!)[0]!, tied: [] }
    }
    const tiedMembers = new Set(leading.flatMap((side) => sideMembers(state, side)))
    return { winner: null, tied: state.players.filter((p) => tiedMembers.has(p)) }
  }

  const territoryCounts: Map<PlayerId, number> = new Map()
  for (const pid of active) {
    territoryCounts.set(pid, state.map.territories.filter((t) => t.ownerId === pid).length)
  }

  const maxCount = Math.max(...territoryCounts.values())
  const leaders = active.filter((p) => territoryCounts.get(p) === maxCount)

  if (leaders.length === 1) {
    return { winner: leaders[0]!, tied: [] }
  }
  return { winner: null, tied: leaders }
}

export interface AttackMoveInput {
  fromTerritoryId: TerritoryId
  toTerritoryId: TerritoryId
  diceCount: number
}

/**
 * Returns whether the current player can make an attack this turn.
 *
 * When called without `move`, checks only the phase and per-turn cap —
 * this is the lightweight check used by the bot driver and the blitz loop.
 *
 * When called with `move`, additionally validates all move preconditions:
 *   - source territory exists and is owned by the acting player
 *   - target territory exists and is NOT owned by the acting player
 *   - source is adjacent to the target
 *   - source has at least 2 armies (enough to attack)
 *   - diceCount is in the valid range [1, min(3, source.armies - 1)]
 *
 * Distinct `reason` strings are returned for each failure so the route can
 * surface a meaningful error message without any inline business logic.
 */
export type AttackMoveCheck = {
  allowed: boolean
  reason?: string
  /** Set only for the dice-range rejection; see DICE_COUNT_OUT_OF_RANGE. */
  code?: typeof DICE_COUNT_OUT_OF_RANGE
  /** The dice count the source can roll right now (with `code`). */
  maxDice?: number
}

export function canAttack(
  state: GameState,
  move?: AttackMoveInput,
): AttackMoveCheck {
  if (state.phase !== 'attack') {
    return { allowed: false, reason: `Expected phase 'attack', current phase is '${state.phase}'` }
  }
  const { maxAttacksPerTurn } = state.config
  if (maxAttacksPerTurn !== null && state.attacksThisTurn >= maxAttacksPerTurn) {
    return { allowed: false, reason: 'Maximum attacks per turn reached' }
  }
  if (move === undefined) return { allowed: true }

  const currentPlayerId = state.players[state.turnIndex] as PlayerId
  const { fromTerritoryId, toTerritoryId, diceCount } = move

  const fromTerritory = state.map.territories.find((t) => t.id === fromTerritoryId)
  if (!fromTerritory) {
    return { allowed: false, reason: `Territory '${fromTerritoryId}' does not exist` }
  }
  const toTerritory = state.map.territories.find((t) => t.id === toTerritoryId)
  if (!toTerritory) {
    return { allowed: false, reason: `Territory '${toTerritoryId}' does not exist` }
  }
  if (fromTerritory.ownerId !== currentPlayerId) {
    return { allowed: false, reason: `You do not own '${fromTerritoryId}'` }
  }
  if (toTerritory.ownerId === currentPlayerId) {
    return { allowed: false, reason: `You already own '${toTerritoryId}'` }
  }
  // Two-way borders PLUS any one-way borders. Maps opt in per territory via
  // `oneWayBorders`; absent means the classic symmetric rule, so every other map
  // is unaffected.
  if (!hasBorderTo(fromTerritory, toTerritoryId)) {
    return { allowed: false, reason: `'${fromTerritoryId}' cannot attack '${toTerritoryId}'` }
  }
  if (fromTerritory.armies < 2) {
    return { allowed: false, reason: 'Source territory must have at least 2 armies to attack' }
  }
  const maxDice = Math.min(3, fromTerritory.armies - 1)
  if (diceCount < 1 || diceCount > maxDice) {
    // Carries a code because this is the one move check a well-behaved client
    // can trip on its own: the dice count was legal a roll ago and the source
    // thinned under it. Routes answer it as a state conflict, not a bad request.
    return { allowed: false, code: DICE_COUNT_OUT_OF_RANGE, maxDice, reason: `diceCount must be between 1 and ${maxDice} (min(3, source.armies-1))` }
  }

  return { allowed: true }
}

export interface FortifyMoveInput {
  fromTerritoryId: TerritoryId
  toTerritoryId: TerritoryId
  armies: number
}

/**
 * Returns whether the current player can return to the attack phase.
 *
 * Guard: phase must be 'fortify' AND fortifiesThisTurn must be 0 (no fortify
 * action has been taken yet this turn). Once any fortify has been committed,
 * the option closes — the player must end their turn instead.
 */
export function canReturnToAttack(state: GameState): { allowed: boolean; reason?: string } {
  if (state.phase !== 'fortify') {
    return { allowed: false, reason: `Expected phase 'fortify', current phase is '${state.phase}'` }
  }
  if (state.fortifiesThisTurn > 0) {
    return { allowed: false, reason: 'Cannot return to attack after a fortify has been made' }
  }
  return { allowed: true }
}

/**
 * Transitions the game state from 'fortify' back to 'attack'.
 *
 * Only valid when state.phase === 'fortify' && state.fortifiesThisTurn === 0.
 * Emits a 'phase-advance' event (from: 'fortify', to: 'attack') so the event
 * log is consistent with forward phase transitions.
 *
 * Pure: never mutates state; returns a new object.
 */
export function returnToAttack(state: GameState): GameState {
  const currentPlayer = state.players[state.turnIndex] as PlayerId
  const phaseAdvanceEvent: GameEvent = {
    kind: 'phase-advance',
    from: 'fortify',
    to: 'attack',
    player: currentPlayer,
    ts: Date.now(),
  }
  return {
    ...state,
    phase: 'attack',
    events: [...(state.events ?? []), phaseAdvanceEvent],
  }
}

/**
 * Returns whether the current player can make a fortify move this turn.
 *
 * When called without `move`, checks only the phase and per-turn cap —
 * the lightweight check used by the bot driver.
 *
 * When called with `move`, additionally validates all move preconditions:
 *   - source territory exists and is owned by the acting player
 *   - destination territory exists and is owned by the acting player (or,
 *     in a team game, by a teammate)
 *   - source is adjacent to the destination
 *   - armies is a positive integer (≥ 1)
 *   - source has enough armies to leave ≥ 1 behind (source.armies > armies)
 *
 * Distinct `reason` strings are returned for each failure.
 */
export function canFortify(
  state: GameState,
  move?: FortifyMoveInput,
): { allowed: boolean; reason?: string } {
  if (state.phase !== 'fortify') {
    return { allowed: false, reason: `Expected phase 'fortify', current phase is '${state.phase}'` }
  }
  const { maxFortifiesPerTurn } = state.config
  if (maxFortifiesPerTurn !== null && state.fortifiesThisTurn >= maxFortifiesPerTurn) {
    return { allowed: false, reason: 'Maximum fortifies per turn reached' }
  }
  if (move === undefined) return { allowed: true }

  const currentPlayerId = state.players[state.turnIndex] as PlayerId
  const { fromTerritoryId, toTerritoryId, armies } = move

  const fromTerritory = state.map.territories.find((t) => t.id === fromTerritoryId)
  if (!fromTerritory) {
    return { allowed: false, reason: `Territory '${fromTerritoryId}' does not exist` }
  }
  const toTerritory = state.map.territories.find((t) => t.id === toTerritoryId)
  if (!toTerritory) {
    return { allowed: false, reason: `Territory '${toTerritoryId}' does not exist` }
  }
  if (fromTerritory.ownerId !== currentPlayerId) {
    return { allowed: false, reason: `You do not own '${fromTerritoryId}'` }
  }
  // The destination may be a teammate's in a team game (plan §5); in
  // free-for-all canPlaceOn is exactly "you own it".
  if (!canPlaceOn(state, currentPlayerId, toTerritory)) {
    return {
      allowed: false,
      reason: state.teams
        ? `'${toTerritoryId}' is not yours or a teammate's`
        : `You do not own '${toTerritoryId}'`,
    }
  }
  // A one-way border is a real border, so armies move along it too — in the
  // outgoing direction only. The far side has no matching edge, so it cannot
  // fortify back.
  if (!hasBorderTo(fromTerritory, toTerritoryId)) {
    return { allowed: false, reason: `'${fromTerritoryId}' does not border '${toTerritoryId}'` }
  }
  if (armies < 1) {
    return { allowed: false, reason: 'armies must be a positive integer' }
  }
  const headroom = armyHeadroom(toTerritory)
  if (armies > headroom) {
    return {
      allowed: false,
      reason: `'${toTerritoryId}' holds at most ${toTerritory.maxArmies} armies (room for ${headroom} more)`,
    }
  }
  if (fromTerritory.armies <= armies) {
    return { allowed: false, reason: `Must leave at least 1 army behind (source has ${fromTerritory.armies}, you are moving ${armies})` }
  }

  return { allowed: true }
}
